/**
 * /auth/sign-in
 *
 * Email-only magic-link sign-in. Submits to supabase-js signInWithOtp via the
 * sendMagicLink helper (lib/auth.ts). Visual register: warm, plainspoken, no
 * marketing chrome. Voice attributes Warm Contrarian + Yes, Please applied to
 * the copy (per systems/voice/SYSTEM.md). No jargon, no security theater.
 *
 * On successful submit the form swaps to a confirmation state so the user
 * knows the link is on its way without leaving the page.
 *
 * Per ARCH-002 §2, magic link is the only sign-in path. There is no password
 * field, no "forgot password" link, no third-party SSO buttons.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { sendMagicLink } from '@/lib/auth';
import { redirectIfSignedIn } from '@/lib/route-guards';

const MARKETING_SITE_URL = 'https://yesplease.studio';

export const Route = createFileRoute('/auth/sign-in')({
  // Already-signed-in users skip the form and go to their role-home. Keeps
  // the back-button case from re-prompting an authenticated subscriber.
  beforeLoad: async () => {
    await redirectIfSignedIn();
  },
  component: SignInPage,
});

function SignInPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Type your email and I will send a link.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const { error: signInError } = await sendMagicLink(trimmed);
    setSubmitting(false);
    if (signInError) {
      setError(signInError.message);
      return;
    }
    setSent(true);
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md space-y-8">
        <header className="space-y-2 text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Yes Please Studio
          </p>
          <h1 className="font-display text-2xl text-foreground">
            Sign in to your portal
          </h1>
        </header>

        {sent ? (
          <ConfirmationState email={email} onReset={() => setSent(false)} />
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourwork.com"
              />
            </div>

            {error ? (
              <p
                className="text-sm text-destructive"
                role="alert"
                aria-live="polite"
              >
                {error}
              </p>
            ) : null}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Sending...' : 'Send my sign-in link'}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              No password, ever. You will get a link by email that signs you in.
            </p>
          </form>
        )}

        <p className="text-center text-sm">
          <a
            href={MARKETING_SITE_URL}
            className="text-muted-foreground underline-offset-4 hover:underline"
          >
            Back to Yes Please Studio
          </a>
        </p>
      </div>
    </main>
  );
}

function ConfirmationState({ email, onReset }: { email: string; onReset: () => void }) {
  return (
    <div className="space-y-4 rounded-md border border-border bg-card p-6 text-sm">
      <p className="font-medium text-foreground">Check your email.</p>
      <p className="text-muted-foreground">
        I sent a sign-in link to <span className="font-medium text-foreground">{email}</span>.
        It works once and expires in an hour.
      </p>
      <p className="text-muted-foreground">
        Wrong address? <button type="button" className="underline" onClick={onReset}>Send to a different email</button>.
      </p>
    </div>
  );
}
